import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ElementCheckPermissionWrapper from '../../../utils/ElementCheckPermissionWrapper';
import pausedIcon from '../../../../assets/images/paused-icon.svg';
import continueIcon from '../../../../assets/images/continue-icon.svg';
import MultiSelectDropdown from './MultiSelectDropdown';
import '../Styles/PointRedemptionPolicyList.scss';
import dayjs from 'dayjs';
import {
    fetchAgencyOptions,
    fetchProviderOptions,
    fetchCompanyOptions,
    fetchPointExchangePolicies,
    fetchPointExchangePolicyDetail,
    togglePointExchangePolicyStatus,
    deletePointExchangePolicy,
} from '../Controllers/api';
import PauseConfirmationModal from './PauseConfirmationModal';
import { getCookie } from '../../../utils/cookies';

const STATUS_CONFIG = {
    all: {
        label: 'Tất cả',
        description: 'Hiển thị toàn bộ chính sách',
    },
    draft: {
        label: 'Lưu tạm',
        description: 'Chưa kích hoạt, có thể chỉnh sửa',
    },
    active: {
        label: 'Đã kích hoạt',
        description: 'Khách hàng có thể quy đổi',
    },
    paused: {
        label: 'Tạm dừng',
        description: 'Ngưng áp dụng tạm thời',
    },
    scheduled: {
        label: 'Sắp kích hoạt',
        description: 'Tự động bật theo lịch',
    },
    archived: {
        label: 'Đã hủy',
        description: 'Không còn sử dụng',
    },
};

const STATUS_CODE_TO_KEY = {
    0: 'archived',
    1: 'active',
    2: 'draft',
    4: 'paused',
};

const STATUS_KEY_TO_CODE = {
    active: 1,
    draft: 2,
    paused: 4,
    archived: 0,
};

const EXCHANGE_TYPE_LABELS = {
    1: 'Voucher giảm giá',
    2: 'Voucher tặng hàng',
    3: 'Giảm giá hóa đơn trực tiếp',
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const STATUS_ORDER = ['all', 'draft', 'active', 'paused', 'archived'];

const DETAIL_ACTIONS = {
    draft: [
        { key: 'copy', label: 'SAO CHÉP', variant: 'copy' },
        { key: 'cancel', label: 'HỦY', variant: 'danger' },
        { key: 'edit', label: 'CHỈNH SỬA', variant: 'primary' },
    ],
    active: [
        { key: 'copy', label: 'SAO CHÉP', variant: 'copy' },
        { key: 'pause', label: 'TẠM DỪNG', variant: 'primary' },
    ],
    paused: [
        { key: 'copy', label: 'SAO CHÉP', variant: 'copy' },
        { key: 'resume', label: 'TIẾP TỤC', variant: 'primary' },
    ],
    default: [{ key: 'copy', label: 'SAO CHÉP', variant: 'copy' }],
};

const getDetailActions = (status) => DETAIL_ACTIONS[status] || DETAIL_ACTIONS.default;
const DETAIL_ACTION_ICONS = {
    copy: 'mdi-content-copy',
    cancel: 'mdi-close-circle',
    edit: 'mdi-pencil',
    pause: 'mdi-pause',
    resume: 'mdi-play',
};

const getExchangeKindFromOfferType = (offerType = '') => {
    const normalized = offerType.toLowerCase();
    if (normalized.includes('hóa đơn')) {
        return 'invoice-discount';
    }
    if (
        normalized.includes('tặng hàng') ||
        normalized.includes('tặng quà') ||
        normalized.includes('tặng kèm')
    ) {
        return 'product-voucher';
    }
    return 'discount-voucher';
};

const formatCompanyDisplay = (companyIds) => {
    if (!companyIds || companyIds.length === 0) {
        return 'Tất cả công ty';
    }
    if (companyIds.length === 1 && companyIds[0] === -1) {
        return 'Tất cả công ty';
    }
    const validCompanyIds = companyIds.filter((id) => id !== -1 && id != null);
    if (validCompanyIds.length === 0) {
        return 'Tất cả công ty';
    }
    return `${validCompanyIds.length} công ty`;
};

const formatAgencyDisplay = (agencyIds) => {
    if (!agencyIds || agencyIds.length === 0) {
        return 'Tất cả chi nhánh';
    }
    if (agencyIds.length === 1 && agencyIds[0] === -1) {
        return 'Tất cả chi nhánh';
    }
    const validAgencyIds = agencyIds.filter((id) => id !== -1 && id != null);
    if (validAgencyIds.length === 0) {
        return 'Tất cả chi nhánh';
    }
    return `${validAgencyIds.length} chi nhánh`;
};

const formatCompanyNamesList = (companyIds, companyOptions) => {
    if (!companyIds || companyIds.length === 0) {
        return 'Tất cả công ty';
    }
    if (companyIds.length === 1 && companyIds[0] === -1) {
        return 'Tất cả công ty';
    }
    const validCompanyIds = companyIds.filter((id) => id !== -1 && id != null);
    if (validCompanyIds.length === 0) {
        return 'Tất cả công ty';
    }

    if (!companyOptions || companyOptions.length === 0) {
        return `${validCompanyIds.length} công ty`;
    }

    const companyNames = validCompanyIds
        .map((id) => {
            const company = companyOptions.find((opt) => opt.value === id);
            return company ? company.label : null;
        })
        .filter((name) => name != null);

    if (companyNames.length === 0) {
        return `${validCompanyIds.length} công ty`;
    }

    return companyNames.join(', ');
};

const formatAgencyNamesList = (agencyIds, branchOptions) => {
    if (!agencyIds || agencyIds.length === 0) {
        return 'Tất cả chi nhánh';
    }
    if (agencyIds.length === 1 && agencyIds[0] === -1) {
        return 'Tất cả chi nhánh';
    }
    const validAgencyIds = agencyIds.filter((id) => id !== -1 && id != null);
    if (validAgencyIds.length === 0) {
        return 'Tất cả chi nhánh';
    }

    if (!branchOptions || branchOptions.length === 0) {
        return `${validAgencyIds.length} chi nhánh`;
    }

    const agencyNames = validAgencyIds
        .map((id) => {
            const agency = branchOptions.find((opt) => opt.value === id);
            return agency ? agency.label : null;
        })
        .filter((name) => name != null);

    if (agencyNames.length === 0) {
        return `${validAgencyIds.length} chi nhánh`;
    }

    return agencyNames.join(', ');
};

const EXPIRATION_UNIT_LABELS = {
    1: 'ngày',
    2: 'tháng',
    3: 'năm',
};

const formatVoucherExpiration = (unit, value) => {
    if (!unit || value == null) {
        return '---';
    }
    const numericUnit = Number(unit);
    const label = EXPIRATION_UNIT_LABELS[numericUnit];
    if (!label) {
        return '---';
    }
    return `${value} ${label}`;
};

// Helper function để format số với dấu chấm phân cách hàng nghìn (1.000, 100.000)
const formatNumberWithDot = (value) => {
    if (!value && value !== 0) return '';
    const num = Number(value);
    if (isNaN(num)) return '';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

// Chuyển chuỗi sang chuỗi đầy đủ
const toPlainNumberString = (value) => {
    if (value === null || value === undefined) return '';
    const str = typeof value === 'number' ? value.toString() : String(value);
    if (!/e/i.test(str)) return str;
    const [mantissaPart, expPart] = str.toLowerCase().split('e');
    const exponent = parseInt(expPart, 10);
    if (Number.isNaN(exponent)) return str;

    const sign = mantissaPart.startsWith('-') ? '-' : '';
    const mantissa = mantissaPart.replace('-', '');
    const [intPart, fracPart = ''] = mantissa.split('.');
    const digits = intPart + fracPart;
    const decimalPos = intPart.length;
    const newDecimalPos = decimalPos + exponent;

    if (exponent >= 0) {
        const zerosToAdd = Math.max(0, newDecimalPos - digits.length);
        return sign + digits + '0'.repeat(zerosToAdd);
    }

    return str;
};

// Rút gọn hiển thị cho số rất lớn: giữ 5 ký tự đầu và 2 ký tự cuối
const formatLargeNumberCompact = (value) => {
    const plain = toPlainNumberString(value);
    if (!plain) return '';
    if (plain.length <= 12) return plain;
    return `${plain.slice(0, 5)}...${plain.slice(-2)}`;
};

const buildPolicyViewModel = (policy, detail, companyOptions = [], branchOptions = [], isLoadingDetail = false) => {
    if (!detail || isLoadingDetail) {
        return {
            ...policy,
            companyDisplay: '',
            agencyDisplay: '',
            companyNamesList: '',
            agencyNamesList: '',
            actionReason: policy?.actionReason ?? '',
        };
    }

    const voucherExpiry =
        detail.voucher?.expirationUnit != null && detail.voucher?.expirationValue != null
            ? formatVoucherExpiration(detail.voucher.expirationUnit, detail.voucher.expirationValue)
            : policy.voucherExpirationText;
    const voucherValue = detail.voucher?.valueAmount ?? detail.directDiscountAmount;
    const valueType = detail.voucher?.valueType != null ? Number(detail.voucher.valueType) : null;

    const quantityRaw = detail.pointsRequired ?? policy.quantityRaw ?? '';
    const quantityDisplay = formatLargeNumberCompact(quantityRaw);

    let voucherValueDisplayText = '';
    if (voucherValue != null) {
        if (valueType === 2) {
            voucherValueDisplayText = `${Number(voucherValue)}%`;
        } else {
            voucherValueDisplayText = `${formatNumberWithDot(voucherValue)} VNĐ`;
        }
    }

    const maxValueReduceDisplay = detail.voucher?.maxValueReduce
        ? `${formatNumberWithDot(detail.voucher.maxValueReduce)} VNĐ`
        : null;

    // Format dates for event start and end
    const eventStartDate = detail.eventStartDate 
        ? dayjs(detail.eventStartDate).format('DD/MM/YYYY')
        : null;
    const eventEndDate = detail.eventEndDate 
        ? dayjs(detail.eventEndDate).format('DD/MM/YYYY')
        : null;
    const allow100PercentDiscount = detail.allow100PercentDiscount ?? 0;
    
    // Format date range display
    const eventDateRangeDisplay = (allow100PercentDiscount === 1 && eventStartDate && eventEndDate)
        ? `${eventStartDate} - ${eventEndDate}`
        : (allow100PercentDiscount === 1 ? '---' : 'Không');

    return {
        ...policy,
        description: detail.description ?? policy.description,
        quantityRaw,
        quantityDisplay,
        maxPerCustomer: detail.maxExchangePerCustomer ?? policy.maxPerCustomer,
        maxTotalRedemptions: detail.maxExchangeTotal ?? policy.maxTotalRedemptions,
        voucherName: detail.voucher?.name ?? policy.voucherName,
        voucherExpirationText: voucherExpiry,
        voucherValueDisplay: voucherValueDisplayText || (detail.voucher ? '---' : policy.voucherValueDisplay),
        maxValueReduceDisplay: maxValueReduceDisplay,
        giftName: detail.voucher?.productName
            ? detail.voucher.productName
            : (detail.voucher?.productId
                ? `Sản phẩm #${detail.voucher.productId}`
                : policy.giftName),
        conversionRateText:
            detail.directDiscountAmount && quantityDisplay
                ? `${quantityDisplay} bing = ${formatNumberWithDot(detail.directDiscountAmount)} VNĐ`
                : policy.conversionRateText,
        companyDisplay: formatCompanyDisplay(detail.companyIds),
        agencyDisplay: formatAgencyDisplay(detail.agencyIds),
        companyNamesList: formatCompanyNamesList(detail.companyIds, companyOptions),
        agencyNamesList: formatAgencyNamesList(detail.agencyIds, branchOptions),
        actionReason: detail.actionReason ?? policy?.actionReason ?? '',
        allow100PercentDiscount: allow100PercentDiscount,
        eventDateRangeDisplay: eventDateRangeDisplay,
    };
};

const PointRedemptionPolicyList = (props) => {
    const history = useHistory();
    const [searchValue, setSearchValue] = useState('');
    const [statusFilters, setStatusFilters] = useState(STATUS_ORDER);
    const [branchFilter, setBranchFilter] = useState([-1]); 
    const [companyFilter, setCompanyFilter] = useState([-1]);
    const [gatewayFilter, setGatewayFilter] = useState('all');
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedPolicyId, setExpandedPolicyId] = useState(null);
    const [activeDetailTab, setActiveDetailTab] = useState('general');
    const [policies, setPolicies] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [policiesLoading, setPoliciesLoading] = useState(false);
    const [policiesError, setPoliciesError] = useState('');
    const [policyDetails, setPolicyDetails] = useState({});
    const [detailLoading, setDetailLoading] = useState({});
    const [actionLoading, setActionLoading] = useState({});
    const [deleteLoading, setDeleteLoading] = useState({});
    const [isActionModalOpen, setIsActionModalOpen] = useState(false);
    const [actionModalReason, setActionModalReason] = useState('');
    const [actionModalReasonError, setActionModalReasonError] = useState('');
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [modalActionType, setModalActionType] = useState('pause'); 
    const MAX_VISIBLE_PAGES = 7;

    const [branchOptions, setBranchOptions] = useState([]);
    const [allBranchOptions, setAllBranchOptions] = useState([]); 

    const gatewayOptions = useMemo(() => {
        const options = Array.from(
            new Set(policies.map((policy) => policy.applyGateway).filter((value) => !!value))
        );
        return [
            { label: 'Tất cả cổng', value: 'all' },
            ...options.map((option) => ({ label: option, value: option })),
        ];
    }, [policies]);

    const [companyOptions, setCompanyOptions] = useState([]);

    const {
        LIST,
        DETAIL,
        CREATE,
        COPY,
        UPDATE,
        PAUSED,
        CONTINUE,
        CANCEL,
    } = props.actions || {};

    // Debounce searchValue để tránh gọi API quá nhiều lần khi gõ
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 500); 

        return () => clearTimeout(timer);
    }, [searchValue]);

    const filteredPolicies = useMemo(() => {
        const keyword = searchValue.trim().toLowerCase();
        const shouldFilterByStatus = statusFilters.length > 0 && !statusFilters.includes('all');

        return policies.filter((policy) => {
            const matchesKeyword =
                keyword.length === 0 ||
                policy.name.toLowerCase().includes(keyword) ||
                (policy.policyCode || '').toLowerCase().includes(keyword) ||
                policy.id.toLowerCase().includes(keyword) ||
                (policy.description || '').toLowerCase().includes(keyword);

            const matchesStatus =
                !shouldFilterByStatus || statusFilters.includes(policy.status);
            const matchesBranch = branchFilter.length === 0 ? true : true;
            const matchesCompany = companyFilter.length === 0 ? true : true;
            const matchesGateway =
                gatewayFilter === 'all' ? true : policy.applyGateway === gatewayFilter;

            return matchesKeyword && matchesStatus && matchesBranch && matchesCompany && matchesGateway;
        });
    }, [policies, searchValue, statusFilters, branchFilter, companyFilter, gatewayFilter]);

    const isInitialMount = useRef(true);
    const isInitialPolicyLoad = useRef(true);
    const lastFetchParams = useRef(null);
    
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        setCurrentPage(1);
    }, [debouncedSearchValue, statusFilters, branchFilter, companyFilter, gatewayFilter, pageSize]);

    // Load chi nhánh và công ty
    useEffect(() => {
        const loadAgenciesAndCompanies = async () => {
            try {
                // Lấy thông tin user từ cookie để kiểm tra super admin
                const userCookie = getCookie();
                const userData = userCookie ? JSON.parse(userCookie) : null;
                const isSuperAdmin = userData !== null && userData.isSuperAdmin === 1;
                
                const companyResponse = await fetchCompanyOptions();
                
                if (companyResponse?.status === 200 && Array.isArray(companyResponse.data?.data)) {
                    const companyList = companyResponse.data.data;
                    let filteredCompanies = companyList;

                    if (!isSuperAdmin) {
                const agencyResponse = await fetchAgencyOptions({
                    companyId: [-1],
                    type: 3,
                });

                if (agencyResponse?.status === 200 && Array.isArray(agencyResponse.data?.agencyInfoResList)) {
                    const agencyOptions = agencyResponse.data.agencyInfoResList.map((item) => ({
                        label: item.name,
                        value: item.id,
                    }));
                    setAllBranchOptions(agencyOptions);

                    const companyNamesSet = new Set();
                    agencyResponse.data.agencyInfoResList.forEach((agency) => {
                        if (agency.companyName) {
                            companyNamesSet.add(agency.companyName);
                        }
                    });

                            filteredCompanies = companyList.filter((company) =>
                            companyNamesSet.has(company.name)
                        );
                        } else {
                            setAllBranchOptions([]);
                        }
                    } else {
                        const [agencyResponse, providerResponse] = await Promise.all([
                            fetchAgencyOptions({
                                companyId: [-1],
                            }),
                            fetchProviderOptions({
                                companyId: [-1],
                            })
                        ]);

                        let allBranches = [];

                        if (agencyResponse?.status === 200 && Array.isArray(agencyResponse.data?.agencyInfoResList)) {
                            allBranches = [...allBranches, ...agencyResponse.data.agencyInfoResList];
                        }

                        if (providerResponse?.status === 200 && Array.isArray(providerResponse.data?.agencyInfoResList)) {
                            allBranches = [...allBranches, ...providerResponse.data.agencyInfoResList];
                        }

                        const uniqueBranches = Array.from(
                            new Map(allBranches.map(item => [item.id, item])).values()
                        );
                        const agencyOptions = uniqueBranches.map((item) => ({
                            label: item.name,
                            value: item.id,
                        }));
                        setAllBranchOptions(agencyOptions);
                    }
                    
                        const options = filteredCompanies.map((item) => ({
                            label: item.name,
                            value: item.id,
                        }));
                        setCompanyOptions([{ label: 'Tất cả', value: -1 }, ...options]);
                } else {
                    setCompanyOptions([]);
                    setAllBranchOptions([]);
                }
            } catch (error) {
                console.error('Failed to load agencies and companies', error);
                setAllBranchOptions([]);
                setCompanyOptions([]);
            }
        };

        loadAgenciesAndCompanies();
    }, []);

    useEffect(() => {
        const loadAgencies = async () => {
            try {
                // Lấy thông tin user từ cookie để kiểm tra super admin
                const userCookie = getCookie();
                const userData = userCookie ? JSON.parse(userCookie) : null;
                const isSuperAdmin = userData !== null && userData.isSuperAdmin === 1;
                
                const selectedCompanyIds =
                    companyFilter.length === 0 || companyFilter.includes(-1) ? [-1] : companyFilter;
                
                let allBranches = [];
                
                if (isSuperAdmin) {
                    const [agencyResponse, providerResponse] = await Promise.all([
                        fetchAgencyOptions({
                            companyId: selectedCompanyIds,
                        }),
                        fetchProviderOptions({
                            companyId: selectedCompanyIds,
                        })
                    ]);

                    if (agencyResponse?.status === 200 && Array.isArray(agencyResponse.data?.agencyInfoResList)) {
                        allBranches = [...allBranches, ...agencyResponse.data.agencyInfoResList];
                    }

                    if (providerResponse?.status === 200 && Array.isArray(providerResponse.data?.agencyInfoResList)) {
                        allBranches = [...allBranches, ...providerResponse.data.agencyInfoResList];
                    }
                } else {
                const response = await fetchAgencyOptions({
                    companyId: selectedCompanyIds,
                    type: 3,
                });
                if (
                    response?.status === 200 &&
                    Array.isArray(response.data?.agencyInfoResList)
                ) {
                        allBranches = [...allBranches, ...response.data.agencyInfoResList];
                    }
                }

                const uniqueBranches = Array.from(
                    new Map(allBranches.map(item => [item.id, item])).values()
                );
                const options = uniqueBranches.map((item) => ({
                        label: item.name,
                        value: item.id,
                    }));
                    setBranchOptions(options);
                    setBranchFilter((prev) => {
                        if (prev.length === 1 && prev[0] === -1) {
                            return prev; 
                        }
                        if (prev.length === 0) {
                            return prev;
                        }
                        const filtered = prev.filter((agencyId) =>
                            agencyId === -1 || options.some((opt) => opt.value === agencyId)
                        );
                        if (filtered.length === prev.length && filtered.every((id, idx) => id === prev[idx])) {
                            return prev;
                        }
                        return filtered;
                    });
            } catch (error) {
                console.error('Failed to fetch agency list', error);
                setBranchOptions([]);
                setBranchFilter((prev) => {
                    if (prev.length === 0) {
                        return prev;
                    }
                    return [];
                });
            }
        };

        loadAgencies();
    }, [companyFilter]);

    useEffect(() => {
        let isMounted = true;
        const loadPolicies = async () => {
            const fetchKey = JSON.stringify({
                debouncedSearchValue,
                statusFilters: [...statusFilters].sort().join(','),
                pageSize,
                currentPage,
                companyFilter: [...companyFilter].sort().join(','),
                branchFilter: [...branchFilter].sort().join(','),
            });
            
            if (lastFetchParams.current === fetchKey) {
                return;
            }
            
            lastFetchParams.current = fetchKey;
            isInitialPolicyLoad.current = false;
            
            setPoliciesLoading(true);
            setPoliciesError('');

            try {
                let statusList = null;

                if (statusFilters.length === 0) {
                    statusList = [404];
                } else if (statusFilters.includes('all')) {
                    statusList = null;
                } else {
                    const activeStatusFilters = statusFilters.filter((status) => status !== 'all');
                    statusList = activeStatusFilters
                        .map((status) => STATUS_KEY_TO_CODE[status])
                        .filter((code) => code != null);

                    if (statusList.length === 0) {
                        statusList = null;
                    }
                }

                const normalizedCompanyFilter =
                    companyFilter.length === 0
                        ? []
                        : companyFilter.includes(-1)
                            ? [-1]
                            : companyFilter;
                const normalizedBranchFilter =
                    branchFilter.length === 0
                        ? []
                        : branchFilter.includes(-1)
                            ? [-1]
                            : branchFilter;

                const hasCompanyFilter = normalizedCompanyFilter.length > 0;
                const hasBranchFilter = normalizedBranchFilter.length > 0;

                const policyPayload = {
                    page: currentPage,
                    size: pageSize,
                    statusList,
                    keyword: debouncedSearchValue.trim() || null,
                };

                if (hasCompanyFilter) {
                    policyPayload.companyIds = normalizedCompanyFilter;
                }

                if (hasBranchFilter) {
                    policyPayload.agencyIds = normalizedBranchFilter;
                }

                // Gọi API một lần với statusList
                const response = await fetchPointExchangePolicies(policyPayload);

                if (!isMounted) {
                    return;
                }

                if (response?.code === 200 && Array.isArray(response.data?.items)) {
                const normalized = response.data.items.map((item) => {
                    const quantityRaw = item?.pointsRequired ?? '';
                    const quantityDisplay = formatLargeNumberCompact(quantityRaw);
                    return {
                        id: item?.id?.toString() ?? '',
                        policyCode: item?.policyCode ?? '',
                        name: item?.policyName ?? '',
                        description: item?.description ?? '',
                        applyBranches: 'Tất cả chi nhánh',
                        applyGateway: 'Tất cả cổng',
                        offerType: EXCHANGE_TYPE_LABELS[item?.exchangeType] ?? 'Không xác định',
                        exchangeType: item?.exchangeType,
                        quantityRaw,
                        quantityDisplay,
                        status: STATUS_CODE_TO_KEY[item?.policyStatus] || 'draft',
                        statusCode: item?.policyStatus,
                        actionReason: item?.actionReason ?? '',
                    };
                });
                    setPolicies(normalized);
                    setTotalElements(response.data?.total || 0);

                    if (isMounted && DETAIL) {
                        loadAllPolicyDetails(normalized);
                    }
                } else {
                    setPolicies([]);
                    setTotalElements(0);
                    setPoliciesError(response?.message || 'Không thể tải danh sách chính sách');
                }
            } catch (error) {
                if (isMounted) {
                    setPolicies([]);
                    setPoliciesError(error?.message || 'Không thể tải danh sách chính sách');
                }
            } finally {
                if (isMounted) {
                    setPoliciesLoading(false);
                }
            }
        };

        loadPolicies();
        return () => {
            isMounted = false;
        };
    }, [debouncedSearchValue, statusFilters, pageSize, currentPage, companyFilter, branchFilter]);

    const pageSizeSelectRef = useRef(null);

    const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
    const pageStart = (currentPage - 1) * pageSize;
    // Sử dụng trực tiếp policies từ API (server-side pagination)
    const paginatedPolicies = policies;
    const visiblePages = useMemo(() => {
        if (totalPages <= MAX_VISIBLE_PAGES) {
            return Array.from({ length: totalPages }, (_, index) => index + 1);
        }

        const pages = [];
        const offset = Math.floor(MAX_VISIBLE_PAGES / 2);
        let start = Math.max(1, currentPage - offset);
        let end = start + MAX_VISIBLE_PAGES - 1;

        if (end > totalPages) {
            end = totalPages;
            start = end - MAX_VISIBLE_PAGES + 1;
        }

        for (let page = start; page <= end; page += 1) {
            pages.push(page);
        }

        return pages;
    }, [currentPage, totalPages]);

    const resultLabel = totalElements > 0
        ? `Hiển thị ${Math.min(pageStart + 1, totalElements)} đến ${Math.min(
            pageStart + policies.length,
            totalElements
        )} trong tổng số ${totalElements} kết quả`
        : 'Không tìm thấy chính sách phù hợp';

    const loadPolicyDetail = async (policyId) => {
        if (!DETAIL) {
            return;
        }
        if (policyDetails[policyId]) {
            return;
        }

        setDetailLoading((prev) => ({ ...prev, [policyId]: true }));
        try {
            const response = await fetchPointExchangePolicyDetail(policyId);
            if (response?.code === 200 && response.data) {
                setPolicyDetails((prev) => ({ ...prev, [policyId]: response.data }));
            } else {
                console.error('Failed to fetch policy detail', response?.message);
            }
        } catch (error) {
            console.error('Failed to fetch policy detail', error);
        } finally {
            setDetailLoading((prev) => ({ ...prev, [policyId]: false }));
        }
    };

    const loadAllPolicyDetails = async (policiesList) => {
        if (!DETAIL) {
            return;
        }
        const detailPromises = policiesList.map((policy) => {
            if (!policyDetails[policy.id]) {
                return fetchPointExchangePolicyDetail(policy.id)
                    .then((response) => {
                        if (response?.code === 200 && response.data) {
                            return { policyId: policy.id, data: response.data };
                        }
                        return null;
                    })
                    .catch((error) => {
                        console.error(`Failed to fetch detail for policy ${policy.id}`, error);
                        return null;
                    });
            }
            return Promise.resolve(null);
        });

        const results = await Promise.all(detailPromises);
        const newDetails = {};
        results.forEach((result) => {
            if (result && result.data) {
                newDetails[result.policyId] = result.data;
            }
        });

        if (Object.keys(newDetails).length > 0) {
            setPolicyDetails((prev) => ({ ...prev, ...newDetails }));
        }
    };

    const renderStatusPill = (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
        return <span className={`status-pill status-pill--${status}`}>{config.label}</span>;
    };

    const toggleStatusFilter = (key) => {
        setStatusFilters((prev) => {
            if (key === 'all') {
                if (prev.includes('all')) {
                    return [];
                }
                return STATUS_ORDER;
            }

            const withoutAll = prev.filter((item) => item !== 'all');
            const exists = withoutAll.includes(key);
            const updated = exists ? withoutAll.filter((item) => item !== key) : [...withoutAll, key];

            if (updated.length === 0) {
                return [];
            }

            if (updated.length === STATUS_ORDER.length - 1) {
                return STATUS_ORDER;
            }

            return updated;
        });
    };

    const changePage = (direction) => {
        setCurrentPage((prev) => {
            if (direction === 'prev') {
                return Math.max(1, prev - 1);
            }
            if (direction === 'next') {
                return Math.min(totalPages, prev + 1);
            }
            return prev;
        });
    };

    const goToPage = (page) => {
        setCurrentPage(() => {
            if (page < 1) {
                return 1;
            }
            if (page > totalPages) {
                return totalPages;
            }
            return page;
        });
    };

    const handlePageSizeIconClick = (event) => {
        event.preventDefault();
        if (!pageSizeSelectRef.current) {
            return;
        }

        const selectEl = pageSizeSelectRef.current;

        if (typeof selectEl.showPicker === 'function') {
            selectEl.showPicker();
            return;
        }

        selectEl.focus();
        selectEl.click();
    };

    const openActionModal = (policy, actionType) => {
        setModalActionType(actionType);
        setSelectedPolicy(policy);
        setActionModalReason('');
        setActionModalReasonError('');
        setIsActionModalOpen(true);
    };

    const resetActionModal = () => {
        setIsActionModalOpen(false);
        setActionModalReason('');
        setActionModalReasonError('');
        setSelectedPolicy(null);
        setModalActionType('pause');
    };

    const handleActionReasonChange = (value) => {
        setActionModalReason(value);
        if (actionModalReasonError && value.trim()) {
            setActionModalReasonError('');
        }
    };


    const handleToggleStatus = async (policy, reason = '') => {
        if (![1, 4].includes(policy.statusCode)) {
            return;
        }
        const sanitizedReason = (reason || '').trim();
        const policyId = policy.id;
        setActionLoading((prev) => ({ ...prev, [policyId]: true }));
        try {
            const response = await togglePointExchangePolicyStatus(policyId, sanitizedReason);
            if (response?.code === 200) {
                const nextStatusCode = policy.statusCode === 1 ? 4 : 1;
                const nextStatusKey = STATUS_CODE_TO_KEY[nextStatusCode] || policy.status;
                const nextActionReason = nextStatusCode === 4 ? sanitizedReason : '';

                setPolicies((prev) =>
                    prev.map((item) =>
                        item.id === policyId
                            ? {
                                ...item,
                                statusCode: nextStatusCode,
                                status: nextStatusKey,
                                actionReason: nextActionReason,
                            }
                            : item
                    )
                );

                setPolicyDetails((prev) => {
                    if (!prev[policyId]) {
                        return prev;
                    }
                    return {
                        ...prev,
                        [policyId]: {
                            ...prev[policyId],
                            policyStatus: nextStatusCode,
                            actionReason: nextActionReason,
                        },
                    };
                });

                resetActionModal();
            } else {
                console.error('Failed to toggle status', response?.message);
            }
        } catch (error) {
            console.error('Failed to toggle status', error);
        } finally {
            setActionLoading((prev) => {
                const nextState = { ...prev };
                delete nextState[policyId];
                return nextState;
            });
        }
    };

    const handleOpenPauseModal = (policy) => {
        if (policy.statusCode === 1) {
            openActionModal(policy, 'pause');
        } else if (policy.statusCode === 4) {
            openActionModal(policy, 'resume');
        }
    };

    const handleOpenCancelModal = (policy) => {
        if (policy.statusCode !== 2) {
            return;
        }
        openActionModal(policy, 'cancel');
    };

    const handleConfirmAction = () => {
        if (!selectedPolicy) {
            return;
        }

        const trimmedReason = actionModalReason.trim();
        const isReasonRequired = modalActionType === 'pause' || modalActionType === 'cancel';

        if (isReasonRequired && !trimmedReason) {
            setActionModalReasonError('Vui lòng nhập lý do');
            return;
        }

        if (modalActionType === 'cancel') {
            handleDeletePolicy(selectedPolicy, trimmedReason);
            return;
        }

        const reasonForToggle = modalActionType === 'pause' ? trimmedReason : '';
        handleToggleStatus(selectedPolicy, reasonForToggle);
    };

    const handleCancelAction = () => {
        resetActionModal();
    };

    const handleDeletePolicy = async (policy, reason = '') => {
        if (policy.statusCode !== 2) {
            return;
        }
        const sanitizedReason = (reason || '').trim();
        const policyId = policy.id;
        setDeleteLoading((prev) => ({ ...prev, [policyId]: true }));
        try {
            const response = await deletePointExchangePolicy(policyId, sanitizedReason);
            if (response?.code === 200) {
                setPolicies((prev) =>
                    prev.map((item) =>
                        item.id === policyId
                            ? {
                                ...item,
                                statusCode: 0,
                                status: 'archived',
                                actionReason: sanitizedReason,
                            }
                            : item
                    )
                );
                setPolicyDetails((prev) => {
                    const currentDetail = prev[policyId];
                    if (!currentDetail) {
                        return prev;
                    }
                    return {
                        ...prev,
                        [policyId]: {
                            ...currentDetail,
                            policyStatus: 0,
                            actionReason: sanitizedReason,
                        },
                    };
                });
                resetActionModal();
            } else {
                console.error('Failed to delete policy', response?.message);
            }
        } catch (error) {
            console.error('Failed to delete policy', error);
        } finally {
            setDeleteLoading((prev) => {
                const nextState = { ...prev };
                delete nextState[policyId];
                return nextState;
            });
        }
    };

    const handleDetailAction = (actionKey, policy, event) => {
        event?.stopPropagation();
        if (actionKey === 'pause' || actionKey === 'resume') {
            handleOpenPauseModal(policy);
        }
        if (actionKey === 'cancel') {
            handleOpenCancelModal(policy);
        }
        if (actionKey === 'copy') {
            history.push(`/point-exchange-policy/create?copyFrom=${policy.id}`);
        }
        if (actionKey === 'edit') {
            history.push(`/point-exchange-policy/update?id=${policy.id}`);
        }
    };

    const handleRowClick = (policyId) => {
        setActiveDetailTab('general');
        setExpandedPolicyId((prev) => {
            const isSame = prev === policyId;
            if (!isSame && !policyDetails[policyId]) {
                loadPolicyDetail(policyId);
            }
            return isSame ? null : policyId;
        });
    };

    const modalLoading = selectedPolicy
        ? modalActionType === 'cancel'
            ? !!deleteLoading[selectedPolicy.id]
            : !!actionLoading[selectedPolicy.id]
        : false;

    return (
        <div className="point-policy-list">
            <div className="page-header">
                <div className="page-title-wrapper">
                    <p>Chính sách đổi điểm</p>
                </div>

                <div className="header-actions">
                    <label className="search-field">
                        <i className="mdi mdi-magnify"></i>
                        <input
                            type="text"
                            placeholder="Tìm tên chính sách, mã chính sách"
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                        />
                    </label>
                    {CREATE && (
                        <button
                            type="button"
                            className="primary-btn"
                            onClick={() => history.push('/point-exchange-policy/create')}
                        >
                            Thêm mới
                        </button>
                    )}
                </div>
            </div>

            <div className="policy-content">
                <aside className="policy-sidebar">
                    <div className="sidebar-card">
                        <div className="card-heading">
                            <span className="card-title">Thông tin chung</span>
                        </div>
                        <MultiSelectDropdown
                            label="Công ty"
                            options={companyOptions}
                            selectedValues={companyFilter}
                            onSelectionChange={setCompanyFilter}
                            placeholder="Chọn công ty"
                        />
                        <MultiSelectDropdown
                            label="Chi nhánh"
                            options={branchOptions}
                            selectedValues={branchFilter}
                            onSelectionChange={setBranchFilter}
                            placeholder="Chọn chi nhánh"
                        />
                    </div>

                    <div className="sidebar-card">
                        <div className="card-heading">
                            <span className="card-title">Trạng thái</span>
                        </div>
                        <div className="status-checkbox-list">
                            {STATUS_ORDER.map((key) => {
                                const config = STATUS_CONFIG[key];
                                const isChecked =
                                    statusFilters.includes('all') || statusFilters.includes(key);

                                return (
                                    <label
                                        key={key}
                                        className={`status-checkbox-item status-checkbox-item--${key}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleStatusFilter(key)}
                                        />
                                        <span>{config.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </aside>

                <section className="policy-table-wrapper">
                    {policiesError && (
                        <div className="table-alert table-alert--error">{policiesError}</div>
                    )}
                    {policiesLoading && (
                        <div className="table-alert table-alert--info">Đang tải dữ liệu...</div>
                    )}
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>Mã chính sách</th>
                                    <th>Tên chính sách</th>
                                    <th>Công ty áp dụng</th>
                                    <th>Chi nhánh áp dụng</th>
                                    <th>Loại quy đổi</th>
                                    <th>Số bing cần đổi</th>
                                    <th>Mô tả</th>
                                    <th>Trạng thái</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!LIST ? (
                                    <tr>
                                        <td colSpan={8} className="empty-state">
                                            Bạn không có quyền xem danh sách chính sách đổi điểm.
                                        </td>
                                    </tr>
                                ) : paginatedPolicies.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="empty-state">
                                            Không có chính sách nào trùng khớp tiêu chí tìm kiếm.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedPolicies.map((policy) => {
                                        const detail = policyDetails[policy.id];
                                        const isLoadingDetail = !!detailLoading[policy.id];
                                        const displayPolicy = buildPolicyViewModel(policy, detail, companyOptions, allBranchOptions, isLoadingDetail);
                                        const actionReasonText = (displayPolicy.actionReason || '').trim();
                                        const shouldShowActionReason =
                                            !!actionReasonText &&
                                            (displayPolicy.status === 'paused' ||
                                                displayPolicy.status === 'archived');
                                        return (
                                            <React.Fragment key={policy.id}>
                                                <tr
                                                    className={`policy-row ${expandedPolicyId === policy.id ? 'policy-row--expanded' : ''
                                                        }`}
                                                    onClick={() => {
                                                        if (!DETAIL) {
                                                            return;
                                                        }
                                                        handleRowClick(policy.id);
                                                    }}
                                                >
                                                    <td>{displayPolicy.policyCode || policy.id}</td>
                                                    <td>{displayPolicy.name}</td>
                                                    <td>{displayPolicy.companyDisplay}</td>
                                                    <td>{displayPolicy.agencyDisplay}</td>
                                                    <td>{displayPolicy.offerType}</td>
                                                    <td>{displayPolicy.quantityDisplay}</td>
                                                    <td>{displayPolicy.description}</td>
                                                    <td>{renderStatusPill(displayPolicy.status)}</td>
                                                </tr>
                                                {expandedPolicyId === policy.id && DETAIL && (
                                                    <tr className="policy-detail-row">
                                                        <td colSpan={8}>
                                                            <div className="policy-detail-card">
                                                                <div className="policy-detail-tabs">
                                                                    <button
                                                                        type="button"
                                                                        className={`detail-tab ${activeDetailTab === 'general'
                                                                                ? 'detail-tab--active'
                                                                                : ''
                                                                            }`}
                                                                        onClick={() => setActiveDetailTab('general')}
                                                                    >
                                                                        THÔNG TIN CHUNG
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className={`detail-tab ${activeDetailTab === 'conversion'
                                                                                ? 'detail-tab--active'
                                                                                : ''
                                                                            }`}
                                                                        onClick={() => setActiveDetailTab('conversion')}
                                                                    >
                                                                        QUY TẮC ĐỔI ĐIỂM
                                                                    </button>
                                                                </div>
                                                                <div
                                                                    className={`policy-detail-body ${activeDetailTab === 'conversion'
                                                                            ? 'policy-detail-body--conversion'
                                                                            : ''
                                                                        }`}
                                                                >
                                                                    {activeDetailTab === 'general' && (
                                                                        <>
                                                                            <div className="detail-column">
                                                                                <div className="detail-row">
                                                                                    <span className="detail-label">
                                                                                        Mã chính sách:
                                                                                    </span>
                                                                                    <span className="detail-value">
                                                                                        {displayPolicy.policyCode || policy.id}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="detail-row">
                                                                                    <span className="detail-label">
                                                                                        Công ty áp dụng:
                                                                                    </span>
                                                                                    <span className="detail-value">
                                                                                        {displayPolicy.companyNamesList || displayPolicy.companyDisplay}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="detail-row">
                                                                                    <span className="detail-label">
                                                                                        Mô tả:
                                                                                    </span>
                                                                                    <span className="detail-value">
                                                                                        {displayPolicy.description}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                            <div className="detail-column">
                                                                                <div className="detail-row">
                                                                                    <span className="detail-label">
                                                                                        Tên chính sách:
                                                                                    </span>
                                                                                    <span className="detail-value">
                                                                                        {displayPolicy.name}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="detail-row">
                                                                                    <span className="detail-label">
                                                                                        Chi nhánh áp dụng:
                                                                                    </span>
                                                                                    <span className="detail-value">
                                                                                        {displayPolicy.agencyNamesList || displayPolicy.agencyDisplay}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="detail-row">
                                                                                    <span className="detail-label">
                                                                                        Trạng thái:
                                                                                    </span>
                                                                                    <span className="detail-value detail-value--status">
                                                                                        {renderStatusPill(displayPolicy.status)}
                                                                                        {shouldShowActionReason && (
                                                                                            <span className="status-reason-note">
                                                                                                (Lý do: {actionReasonText})
                                                                                            </span>
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    )}

                                                                    {activeDetailTab === 'conversion' && (
                                                                        <div className="conversion-detail">
                                                                            <div className="detail-row detail-row--full">
                                                                                <span className="detail-label">
                                                                                    Loại quy đổi:
                                                                                </span>
                                                                                <span className="detail-value">
                                                                                    {displayPolicy.offerType}
                                                                                </span>
                                                                            </div>

                                                                            {(() => {
                                                                                const kind = getExchangeKindFromOfferType(
                                                                                    displayPolicy.offerType
                                                                                );

                                                                                if (kind === 'invoice-discount') {
                                                                                    return (
                                                                                        <div className="conversion-section">
                                                                                            <div className="conversion-section-title">
                                                                                                Thông tin quy đổi
                                                                                            </div>
                                                                                            <div className="detail-row detail-row--full">
                                                                                                <span className="detail-label">
                                                                                                    Tỷ lệ quy đổi:
                                                                                                </span>
                                                                                                <span className="detail-value">
                                                                                                    {displayPolicy.conversionRateText ||
                                                                                                        '---'}
                                                                                                </span>
                                                                                            </div>
                                                                                            <div className="detail-row detail-row--full">
                                                                                                <span className="detail-label">
                                                                                                    Giảm đến 100% giá trị đơn hàng:
                                                                                                </span>
                                                                                                <span className="detail-value">
                                                                                                    {displayPolicy.eventDateRangeDisplay || 'Không'}
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                }

                                                                                if (kind === 'product-voucher') {
                                                                                    return (
                                                                                        <div className="conversion-section">
                                                                                            <div className="conversion-section-title">
                                                                                                Thông tin quy đổi
                                                                                            </div>
                                                                                            <div className="conversion-grid">
                                                                                                <div className="detail-column">
                                                                                                    <div className="detail-row">
                                                                                                        <span className="detail-label">
                                                                                                            Tên voucher:
                                                                                                        </span>
                                                                                                        <span className="detail-value">
                                                                                                            {displayPolicy.voucherName ||
                                                                                                                displayPolicy.name}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                    <div className="detail-row">
                                                                                                        <span className="detail-label">
                                                                                                            Hạn sử dụng voucher:
                                                                                                        </span>
                                                                                                        <span className="detail-value">
                                                                                                            {displayPolicy
                                                                                                                .voucherExpirationText ||
                                                                                                                '---'}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                    <div className="detail-row">
                                                                                                        <span className="detail-label">
                                                                                                            Số lượng đổi tối đa:
                                                                                                        </span>
                                                                                                        <span className="detail-value">
                                                                                                            {displayPolicy
                                                                                                                .maxTotalRedemptions ??
                                                                                                                '---'}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                </div>

                                                                                                <div className="detail-column">
                                                                                                    <div className="detail-row">
                                                                                                        <span className="detail-label">
                                                                                                            Hàng tặng:
                                                                                                        </span>
                                                                                                        <span className="detail-value">
                                                                                                            {displayPolicy.giftName ||
                                                                                                                '---'}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                    <div className="detail-row">
                                                                                                        <span className="detail-label">
                                                                                                            Số bing cần đổi:
                                                                                                        </span>
                                                                                                        <span className="detail-value">
                                                                                                            {displayPolicy.quantityDisplay} bing
                                                                                                        </span>
                                                                                                    </div>
                                                                                                    <div className="detail-row">
                                                                                                        <span className="detail-label">
                                                                                                            Số lượng đổi tối đa/khách
                                                                                                            hàng:
                                                                                                        </span>
                                                                                                        <span className="detail-value">
                                                                                                            {displayPolicy.maxPerCustomer ??
                                                                                                                1}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                }

                                                                                // discount-voucher (default)
                                                                                return (
                                                                                    <div className="conversion-section">
                                                                                        <div className="conversion-section-title">
                                                                                            Thông tin quy đổi
                                                                                        </div>
                                                                                        <div className="conversion-grid">
                                                                                            <div className="detail-column">
                                                                                                <div className="detail-row">
                                                                                                    <span className="detail-label">
                                                                                                        Tên voucher:
                                                                                                    </span>
                                                                                                    <span className="detail-value">
                                                                                                        {displayPolicy.voucherName ||
                                                                                                            displayPolicy.name}
                                                                                                    </span>
                                                                                                </div>
                                                                                                <div className="detail-row">
                                                                                                    <span className="detail-label">
                                                                                                        Hạn sử dụng voucher:
                                                                                                    </span>
                                                                                                    <span className="detail-value">
                                                                                                        {displayPolicy
                                                                                                            .voucherExpirationText ||
                                                                                                            '---'}
                                                                                                    </span>
                                                                                                </div>
                                                                                                <div className="detail-row">
                                                                                                    <span className="detail-label">
                                                                                                        Số lượng đổi tối đa:
                                                                                                    </span>
                                                                                                    <span className="detail-value">
                                                                                                        {displayPolicy
                                                                                                            .maxTotalRedemptions ??
                                                                                                            '---'}
                                                                                                    </span>
                                                                                                </div>
                                                                                                <div className="detail-row">
                                                                                                    <span className="detail-label">
                                                                                                        Giảm đến 100% giá trị đơn hàng:
                                                                                                    </span>
                                                                                                    <span className="detail-value">
                                                                                                        {displayPolicy.eventDateRangeDisplay || 'Không'}
                                                                                                    </span>
                                                                                                </div>
                                                                                            </div>

                                                                                            <div className="detail-column">
                                                                                                <div className="detail-row">
                                                                                                    <span className="detail-label">
                                                                                                        Giá trị voucher:
                                                                                                    </span>
                                                                                                    <span className="detail-value">
                                                                                                        {displayPolicy
                                                                                                            .voucherValueDisplay ||
                                                                                                            '---'}
                                                                                                    </span>
                                                                                                </div>
                                                                                                {displayPolicy.maxValueReduceDisplay && (
                                                                                                    <div className="detail-row">
                                                                                                        <span className="detail-label">
                                                                                                            Giá trị VNĐ giảm tối đa:
                                                                                                        </span>
                                                                                                        <span className="detail-value">
                                                                                                            {displayPolicy.maxValueReduceDisplay}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                )}
                                                                                                <div className="detail-row">
                                                                                                    <span className="detail-label">
                                                                                                        Số bing cần đổi:
                                                                                                    </span>
                                                                                                    <span className="detail-value">
                                                                                                        {displayPolicy.quantityDisplay} bing
                                                                                                    </span>
                                                                                                </div>
                                                                                                <div className="detail-row">
                                                                                                    <span className="detail-label">
                                                                                                        Số lượng đổi tối đa/khách
                                                                                                        hàng:
                                                                                                    </span>
                                                                                                    <span className="detail-value">
                                                                                                        {displayPolicy.maxPerCustomer ??
                                                                                                            1}
                                                                                                    </span>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="detail-actions">
                                                                    {getDetailActions(displayPolicy.status).map((action) => {
                                                                        const isPauseAction = action.key === 'pause';
                                                                        const isResumeAction = action.key === 'resume';
                                                                        const isCancelAction = action.key === 'cancel';
                                                                        const isEditAction = action.key === 'edit';
                                                                        const isCopyAction = action.key === 'copy';

                                                                        // SAO CHÉP dùng quyền riêng COPY
                                                                        if (isCopyAction && !COPY) return null;
                                                                        if (isPauseAction && !PAUSED) return null;
                                                                        if (isResumeAction && !CONTINUE) return null;
                                                                        if (isCancelAction && !CANCEL) return null;
                                                                        if (isEditAction && !UPDATE) return null;

                                                                        const isToggleAction = isPauseAction || isResumeAction;
                                                                        const isDeleteAction = isCancelAction;
                                                                        const isLoading =
                                                                            (isToggleAction && actionLoading[displayPolicy.id]) ||
                                                                            (isDeleteAction && deleteLoading[displayPolicy.id]);

                                                                        return (
                                                                            <button
                                                                                key={action.key}
                                                                                type="button"
                                                                                className={`detail-action-btn detail-action-btn--${action.variant}`}
                                                                                disabled={isToggleAction && isLoading}
                                                                                onClick={(event) => {
                                                                                    handleDetailAction(action.key, displayPolicy, event);
                                                                                }}
                                                                            >
                                                                                {isPauseAction ? (
                                                                                    <img
                                                                                        src={pausedIcon}
                                                                                        alt=""
                                                                                        aria-hidden="true"
                                                                                    />
                                                                                ) : isResumeAction ? (
                                                                                    <img
                                                                                        src={continueIcon}
                                                                                        alt=""
                                                                                        aria-hidden="true"
                                                                                    />
                                                                                ) : (
                                                                                    <i
                                                                                        className={`mdi ${DETAIL_ACTION_ICONS[action.key] || 'mdi-dots-horizontal'}`}
                                                                                        aria-hidden="true"
                                                                                    ></i>
                                                                                )}
                                                                                <span>{action.label}</span>
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="table-footer">
                        <span className="result-info">{resultLabel}</span>


                        <div className="rows-per-page">
                            <span>Hiển thị</span>
                            <div className="select-wrapper">
                                <select
                                    ref={pageSizeSelectRef}
                                    value={pageSize}
                                    onChange={(event) => setPageSize(Number(event.target.value))}
                                >
                                    {PAGE_SIZE_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                                <span className="select-separator"></span>
                                <i
                                    className="mdi mdi-chevron-down"
                                    role="button"
                                    tabIndex={0}
                                    onMouseDown={handlePageSizeIconClick}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            handlePageSizeIconClick(event);
                                        }
                                    }}
                                ></i>
                            </div>
                            <span>kết quả</span>
                        </div>

                        <div className="pagination">
                            <button
                                type="button"
                                className="pagination-control"
                                onClick={() => changePage('prev')}
                                disabled={currentPage === 1}
                                aria-label="Trang trước"
                            >
                                ‹
                            </button>

                            {visiblePages.map((page) => (
                                <button
                                    key={page}
                                    type="button"
                                    className={`pagination-number ${currentPage === page ? 'is-active' : ''
                                        }`}
                                    onClick={() => goToPage(page)}
                                    aria-current={currentPage === page ? 'page' : undefined}
                                >
                                    {page}
                                </button>
                            ))}

                            <button
                                type="button"
                                className="pagination-control"
                                onClick={() => changePage('next')}
                                disabled={currentPage === totalPages}
                                aria-label="Trang sau"
                            >
                                ›
                            </button>
                        </div>
                    </div>
                </section>
            </div>

            {/* Modal xác nhận tạm dừng/tiếp tục */}
            <PauseConfirmationModal
                isOpen={isActionModalOpen}
                reason={actionModalReason}
                reasonError={actionModalReasonError}
                onReasonChange={handleActionReasonChange}
                onConfirm={handleConfirmAction}
                onCancel={handleCancelAction}
                isLoading={modalLoading}
                actionType={modalActionType}
            />
        </div>
    );
};

export default PointRedemptionPolicyList;

